import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { decode } from "base-64";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import { useOrganizationVehiclesQuery } from "../../api/network/adminApiServices";
import { RESPONSE_CODE } from "../../api/network/constants";
import { handleAuthErrorCode } from "../../api/network/errorCodes";
import {
  OverviewApiTags,
  reducerPath,
} from "../../api/network/overviewApiServices";
import {
  downloadReport,
  useDeleteCustomReportMutation,
  useGetReportAllFieldsQuery,
  useGetReportSpecificFieldsQuery,
  useLazyInitReportDownloadQuery,
  useRunCustomReportQuery,
  useRunReportQuery,
} from "../../api/network/reportApiServices";
import { useGetReportProcessStatusQuery } from "../../api/network/schedularApi";
import {
  shouldGiveUpAfterFinalCheck,
  shouldRunFinalCheck,
} from "./downloadPolling";
import {
  setListingQueryParams,
  setModalsData,
  setReportPageSize,
  TListingQueryParams,
  TModalsState,
} from "../../api/store/commonSlice";
import {
  ModifyCustomReportPayloadField,
  RunReportDownloadProcessReponse,
  StandardReportFieldResponse,
  TReport,
  TReportField,
  TReportFilterRequired,
} from "../../api/types/Report";
import svgIcons from "../../assets/svg/svgIcons";
import {
  AdminFormFieldDropdownButton,
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../components/admin/formFields";
import ModalDeleteConfirmation from "../../components/admin/modalDeleteConfirmation";
import ErrorBoundaryForSection from "../../components/errorBoundaryForSection";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
} from "../../components/modalEditListingColumns";
import { ReportErrorModal } from "../../components/reportErrorModal";
import ReportTable, {
  ReportTableColumn,
  ReportTableIndexColumn,
} from "../../components/reports/reportTable";
import SortColumnsButton from "../../components/sortColumnsButton";
import ColorModeButton from "../../components/reports/colorModeButton";
import SortingFilter from "../../components/sortingFilter";
import { BulkStatusModal } from "../../components/statusModals";
import {
  APP_CONFIG,
  MODAL_TYPE,
  REPORT_CATEGORIES,
  REPORT_FILTER_BY_OPTION_FIELD_NAME,
  REPORT_FILTER_BY_OPTION_VALUES,
  REPORT_TYPES,
  SCHEDULER_PROCESS_STATUS,
} from "../../constants/constants";
import useConversion from "../../hooks/conversion";
import { useAccountTimezone } from "../../hooks/timezone";
import { routeUrls } from "../../navigation/routeUrls";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../utils/dateTime";
import { localStorageKeys } from "../../utils/localStorageItems";
import { useLoggedInUserData } from "../../utils/user";
import AdvancedFiltersBar from "./components/advancedFiltersBar";
import AdvanceFilterPopUp from "./components/advanceFilter";
import HeaderBar from "./components/headerBar";
import { AGGREGATE_FUNCTION } from "./components/popupReportColumnAggregation";
import ReportHeaderFiltersDisplayLabel from "./components/reportHeaderFiltersDisplayLabel";
import ReportOptions, {
  TReportOptionsSubmitCallback,
} from "./components/reportOptions";
import SubHeaderBar from "./components/subHeaderBar";
import {
  downloadOptionsFor,
  DROPPABLE_ID_PREFIX_REPORT_COLUMN,
  REPORT_FIELD_OUTPUT_TYPES,
  REPORT_FIELD_OUTPUT_TYPES_NUMBER_ALIGNMENT,
  REPORT_FILTER_OPERATOR_TYPES,
  REPORT_FILTER_TYPES,
  REPORT_GROUP_BY_LEVEL_SLUGS,
  SHARE_OPTIONS,
  STANDARD_REPORT_TIME_RANGE_TIMESTAMP,
  COLOR_MODE_BATCH_SIZE,
  COLOR_MODE_REPORT_IDENTIFIERS,
} from "./constants";
import { useCreateCustomReportContext } from "./context/createCustomReport.context";
import ModalNewCustomReportsStep2 from "./createCustomReport/modalCreateCustomReportsStep2";
import ModalMakeCustomReport from "./createCustomReport/modalMakeCustomReport";
import {
  TCreateCustomReportCallbackProps,
  TModifyCustomReportCallbackProps,
  useReportUtils,
} from "./hooks/reportUtils";
import ModalMakeCopy from "./modalMakeCopy";
import ModalShareReportInternally from "./modalShareReportInternally";
import ModalShareViaEmail from "./modalShareViaEmail";
import { filterIsMultiSelect } from "./reportUtil";
import ModalReportOptions from "./selectAReport/modalReportOptions";
import { TReportTypes } from "./type";
import {
  convertToReportTableColumn,
  getFieldSpecificFilterOptions,
} from "./util";

interface Props {
  // Define the props for your component here
}

const debugComponent = false;

// The color mode choice is remembered per standard report in this browser.
const readColorModePreference = (reportIdentifier: string): boolean => {
  try {
    return (
      localStorage.getItem(localStorageKeys.reportColorMode(reportIdentifier)) ===
      "1"
    );
  } catch {
    return false;
  }
};
const writeColorModePreference = (reportIdentifier: string, on: boolean) => {
  try {
    localStorage.setItem(
      localStorageKeys.reportColorMode(reportIdentifier),
      on ? "1" : "0"
    );
  } catch {
    // storage unavailable (private mode, blocked): the choice lasts for this page only
  }
};

const ScreenSingleReport: React.FC<Props> = () => {
  const {
    reportType,
    categoryId,
    reportId,
    reportFilters: urlParamReportFilters,
  } = useParams<{
    reportType: string;
    categoryId: string;
    reportId: string;
    reportFilters: string;
  }>();
  const { t: tReports } = useTranslation("translation", {
    keyPrefix: "reports",
  });

  const { t: tToast } = useTranslation("translation", {
    keyPrefix: "admins.import_export.toast",
  });
  const { t: tMain } = useTranslation();

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");

  const { data: vehiclesList } = useOrganizationVehiclesQuery({
    accountid: `${thisUserOrganizationId}`,
    page: 1,
    page_size: 5000,
    isactive: 1,
  });

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const reportPageSizes = useSelector(
    (state: any) => state.commonReducer.reportPageSizes
  );
  const { reports: orgReportQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [modalType, setModalType] = useState<MODAL_TYPE>(null);

  // retrieve account timezone
  const { tzForDateFns } = useAccountTimezone();

  const reportsByType: { [key: string]: TReport[] } = useSelector(
    (state: any) => state.commonReducer.reportsByType
  );
  const reportVisibleColumns: { [key: string]: string[] } = useSelector(
    (state: any) => state.commonReducer.reportVisibleColumns
  );

  const {
    setSelectedReportId,
    columnsSearchKeyword,
    setColumnsSearchKeyword,
    columnsOperator,
    setColumnsOperator,
    setSpecificReportResponse,
    setRunReportParams,
    skipRunReport,
    setSkipRunReport,
    groupByColumns,
    setGroupByColumns,
    subGroupByColumns,
    setSubGroupByColumns,
    reportName,
    selectedFields,
    setSelectedFields,
    selectedReportId,
    resetCreateCustomReportForms,
    columnsVisibility,
    setColumnsVisibility,
  } = useCreateCustomReportContext();

  // if reportId changes reset columnSearchKeyword
  useEffect(() => {
    setColumnsSearchKeyword({});
  }, [reportId]);

  const { convertToUnit, DisplayConvertedValue } = useConversion();

  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [showShareDropdown, setShowShareDropdown] = useState(false);

  const [groupByColumnsState, setgroupByColumnsState] =
    useState<ReportTableColumn[]>();
  // Columns
  // columnsVisibility being used instead // const [columns, setColumns] = React.useState<TListingColumn[]>([]);
  // const savedColumns = getLocalStorageItem(localStorageKeys.columns.Reports(reportType ?? "", categoryId ?? "", reportId ?? ""));
  const [savedColumns, setSavedColumns] = useState<string[] | undefined>(
    undefined
  );
  useEffect(() => {
    const reportColumnsKey = localStorageKeys.columns.Reports(
      reportType ?? "",
      categoryId ?? "",
      reportId ?? ""
    );
    setSavedColumns(
      reportVisibleColumns[reportColumnsKey] // || getLocalStorageItem(reportColumnsKey)
    );
  }, [reportId, categoryId, reportType, reportVisibleColumns]);

  const {
    data: dataSpecificReport,
    isFetching: isFetchingSpecificReport,
    isError: isSpecificReportError,
    error: specificReportError,
    refetch: refetchDataSpecificReport,
  } = useGetReportSpecificFieldsQuery(
    {
      type: REPORT_TYPES[reportType as keyof typeof REPORT_TYPES],
      report_id: parseInt(reportId as string),
    },
    {
      skip:
        reportId === "" ||
        reportType === "" || // || savedColumns === undefined
        !reportsByType[reportType as TReportTypes] ||
        !Array.isArray(reportsByType[reportType as TReportTypes]),
    }
  );

  // Color mode (dark, colored statuses, all rows in one scrolling list) for the reports in
  // COLOR_MODE_REPORT_IDENTIFIERS; a custom report qualifies through its linked standard report.
  const standardReportIdentifier = useMemo(() => {
    if (!dataSpecificReport) return undefined;
    if (!dataSpecificReport.linked_standard_report)
      return dataSpecificReport.identifier;
    return Object.values(reportsByType || {})
      .flat()
      .find(
        (report: TReport) =>
          report?.id === dataSpecificReport.linked_standard_report
      )?.identifier;
  }, [dataSpecificReport, reportsByType]);
  const colorModeAvailable =
    !!standardReportIdentifier &&
    COLOR_MODE_REPORT_IDENTIFIERS.includes(standardReportIdentifier);
  const [colorModePreference, setColorModePreference] = useState<
    Record<string, boolean>
  >({});
  const colorMode =
    colorModeAvailable &&
    (colorModePreference[standardReportIdentifier as string] ??
      readColorModePreference(standardReportIdentifier as string));

  // reset the report query params when fetching specific report
  // helps in clearing stale data from the store
  useEffect(() => {
    if (isFetchingSpecificReport) {
      // before that, reset the skipRunReport to true - skip the API call unless fields are set
      setSkipRunReport(true);
      // finally... clear stale data
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          reports: {
            id: "",
            fields: [],
            page: 1,
            page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
            search: "",
          },
        })
      );
    }
  }, [isFetchingSpecificReport]);

  const sharedBy = useMemo(() => {
    const sharedReports = reportsByType[REPORT_TYPES.SHARED_WITH_ME];
    if (Array.isArray(sharedReports)) {
      return sharedReports.find(
        (report) => report.id === parseInt(reportId as string)
      )?.shared_by;
    }
  }, [reportsByType]);

  // if requested report is not found, navigate to all reports
  useEffect(() => {
    if (
      isSpecificReportError &&
      (specificReportError as FetchBaseQueryError)?.status ===
        RESPONSE_CODE.NOT_FOUND
    ) {
      navigate(routeUrls.dashboardChildren.reportsChildren.select_a_type);
    }
  }, [isSpecificReportError, specificReportError]);

  useEffect(() => {
    if (dataSpecificReport?.report_fields) {
      // && savedColumns
      setColumnsVisibility(
        dataSpecificReport?.report_fields?.map((_col: TReportField) => {
          return {
            name: _col.field.name,
            show:
              !!savedColumns && savedColumns?.length > 0
                ? savedColumns.includes(_col.field.name)
                : true,
            // disabled flag prevents show/hide for the column
            disabled:
              // column selected as group by
              _col.field.name === groupByColumns[0]?.header ||
              // column selected as sub group by
              !!subGroupByColumns.find(
                (subCol) => subCol.header === _col.field.name
              ) ||
              // column has aggregate function
              !!_col.aggregate_function,
          };
        })
      );
    }
  }, [
    dataSpecificReport?.report_fields,
    savedColumns,
    groupByColumns,
    subGroupByColumns,
  ]);

  // Get report params from URL params
  const valueString = decode(urlParamReportFilters || ""); // Decode filters from params
  const rawParamJson = !!urlParamReportFilters ? JSON.parse(valueString) : {}; // Param list for the report
  const paramJson =
    // attempting to convert param values to arrays if config in APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS for param bears the multiple flag
    Object.keys(rawParamJson).reduce((acc, key) => {
      if (key === "time_toggle") {
        acc[key] = rawParamJson[key];
      } else {
        const field_config = Object.keys(
          APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
        ).find(
          (config_key) =>
            APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
              config_key as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
            ]?.field_name === key
        );
        const isMulti =
          APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
            field_config as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
          ]?.multiple;
        acc[key] = Array.isArray(rawParamJson[key])
          ? rawParamJson[key]
          : isMulti
            ? rawParamJson[key] === ""
              ? []
              : rawParamJson[key].split(",").map((item: string) => {
                  return { value: item, label: item };
                })
            : rawParamJson[key];
      }
      return acc;
    }, {} as any);
  // start: non-dynamic hack for groups, assets, and the filter_by field
  if (
    Array.isArray(paramJson?.device_groups) &&
    paramJson?.device_groups?.length === 0
  ) {
    if (
      paramJson?.device_id === "" ||
      // even if all assets toggle is selected and no asset is selected, we consider it to be "All Groups" case
      (Array.isArray(paramJson?.device_id) && paramJson?.device_id.length === 0)
    ) {
      paramJson.device_groups_all = true;
      paramJson.filter_by = REPORT_FILTER_BY_OPTION_VALUES.GROUP;
    } else {
      // paramJson.filter_by = REPORT_FILTER_BY_OPTION_VALUES.ASSET; // disabled as this was preventing display of select all groups option
      // set filter_by only if device_id is present as a report filter
      if (
        dataSpecificReport?.report_filters?.find(
          (filter: TReportFilterRequired) =>
            filter.param === REPORT_FILTER_BY_OPTION_FIELD_NAME.ASSET
        )
      ) {
        paramJson.filter_by = REPORT_FILTER_BY_OPTION_VALUES.ASSET;
      }
    }
  }
  // end: non-dynamic hack for groups, assets, and the filter_by field
  const { time_toggle, start_time, end_time, nonce, ...restParams } = paramJson; // exclude items from API call

  const deviceIdIsMutliSelect =
    !!dataSpecificReport?.report_filters &&
    !!filterIsMultiSelect(
      paramJson?.device_id,
      dataSpecificReport?.report_filters?.filter(
        (filter) => filter.filter_type === REPORT_FILTER_TYPES.BASIC
      ) || []
    );
  const deviceGroupIsMultiSelect =
    !!dataSpecificReport?.report_filters &&
    !!filterIsMultiSelect(
      paramJson?.device_groups,
      dataSpecificReport?.report_filters?.filter(
        (filter) => filter.filter_type === REPORT_FILTER_TYPES.BASIC
      ) || []
    );
  const paramJsonForReportOptions = {
    ...paramJson,

    // if device_id value is *, set it to [] and set device_id_all to true
    ...(!!paramJson?.device_id
      ? {
          device_id: [
            APP_CONFIG.REPORTS.GROUP_ASSET_ALL_VALUE,
            APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE,
          ].includes(paramJson.device_id?.[0]?.value)
            ? []
            : // otherwise ensure value is array only if multi-select, otherwise comma-separated string
              deviceIdIsMutliSelect || paramJson.device_id === ""
              ? paramJson.device_id
              : paramJson.device_id
                  ?.map((item: { label: string; value: string }) => item?.value)
                  .join(","),
          device_id_all: [
            APP_CONFIG.REPORTS.GROUP_ASSET_ALL_VALUE,
            APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE,
          ].includes(paramJson.device_id?.[0]?.value)
            ? true
            : paramJson.device_id_all,
        }
      : {}),

    // if device_groups value is *, set it to [] and set device_groups_all to true
    ...(!!paramJson?.device_groups
      ? {
          device_groups: [
            APP_CONFIG.REPORTS.GROUP_ASSET_ALL_VALUE,
            APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE,
          ].includes(paramJson.device_groups?.[0]?.value)
            ? []
            : // otherwise ensure value is array only if multi-select, otherwise comma-separated string
              deviceGroupIsMultiSelect || paramJson.device_groups === ""
              ? paramJson.device_groups
              : paramJson.device_groups
                  ?.map((item: { label: string; value: string }) => item?.value)
                  .join(","),
          device_groups_all: [
            APP_CONFIG.REPORTS.GROUP_ASSET_ALL_VALUE,
            APP_CONFIG.REPORTS.GROUP_ASSET_ALL_ACTIVE_VALUE,
          ].includes(paramJson.device_groups?.[0]?.value)
            ? true
            : paramJson.device_groups_all,
        }
      : {}),
  };

  const filteredAPIParams = {} as any;
  for (const key in restParams) {
    if (key !== "time_toggle" && !key.includes("_status_filter")) {
      const field_config = Object.keys(
        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
      ).find(
        (config_key) =>
          APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
            config_key as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
          ]?.field_name === key
      );
      const isMulti =
        APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS[
          field_config as keyof typeof APP_CONFIG.REPORTS.BASIC_DROPDOWN_FILTERS
        ]?.multiple;
      if (isMulti) {
        // if the field is multi-select, convert the array of objects to a comma-separated string
        filteredAPIParams[key as keyof typeof restParams] = restParams[
          key as keyof typeof restParams
        ]
          .map((item: { label: string; value: string }) => item?.value)
          .join(",");
      } else {
        // if the field is not multi-select, just pass the value as is
        filteredAPIParams[key as keyof typeof restParams] =
          restParams[key as keyof typeof restParams];
      }
    }
  }
  const filteredAPIParamJson = {} as any;
  for (const key in paramJson) {
    if (key !== "time_toggle" && !key.includes("_status_filter")) {
      filteredAPIParamJson[key as keyof typeof paramJson] =
        paramJson[key as keyof typeof paramJson];
    }
  }

  // STANDARD REPORT - Update report query params
  useEffect(() => {
    if (
      !!reportId &&
      reportType === REPORT_TYPES.STANDARD &&
      !isFetchingSpecificReport
    ) {
      const savedPageSize = reportType
        ? reportPageSizes[reportType] || APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE
        : APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE;
      setSkipRunReport(true);
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          reports: {
            ...orgReportQueryParams,
            fields: [], // reset fields to empty array for standard reports
            id: reportId,
            page: 1, // reset page number to 1
            page_size: colorMode ? COLOR_MODE_BATCH_SIZE : savedPageSize,
            ...(!!start_time ? { start_time } : {}),
            ...(!!end_time ? { end_time } : {}),
            ...filteredAPIParams,
          },
        })
      );
      // finally when fields are set, set skipRunReport to false to trigger API call
      setSkipRunReport(false);
      if (APP_CONFIG.DEBUG.REPORTS)
        console.info("All sparkles and rainbows - STANDARD");
    } else {
      if (APP_CONFIG.DEBUG.REPORTS)
        console.error(
          "reportId is empty or report_fields are not available - STANDARD"
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, urlParamReportFilters, isFetchingSpecificReport]);

  // CUSTOM REPORT - Update report query params
  useEffect(() => {
    if (
      dataSpecificReport?.report_fields &&
      reportType !== REPORT_TYPES.STANDARD &&
      !!reportId &&
      !isFetchingSpecificReport
    ) {
      const savedPageSize = reportType
        ? reportPageSizes[reportType] || APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE
        : APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE;
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          reports: {
            ...orgReportQueryParams,
            id: reportId,
            page: 1, // reset page number to 1
            page_size: colorMode ? COLOR_MODE_BATCH_SIZE : savedPageSize,
            fields: dataSpecificReport?.report_fields
              ?.map((field: TReportField) => field.field.id)
              .join(","), // TODO: need to check
            ...(!!start_time ? { start_time } : {}),
            ...(!!end_time ? { end_time } : {}),
            ...filteredAPIParams,
          },
        })
      );
      // finally when fields are set, set skipRunReport to false to trigger API call
      setSkipRunReport(false);
      if (APP_CONFIG.DEBUG.REPORTS)
        console.info("All sparkles and rainbows - CUSTOM");
    } else {
      if (APP_CONFIG.DEBUG.REPORTS)
        console.error(
          "reportId is empty or report_fields are not available - CUSTOM"
        );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataSpecificReport?.report_fields,
    reportId,
    urlParamReportFilters,
    isFetchingSpecificReport,
  ]);

  // update specific report response in redux when dataSpecificReport is fetched
  useEffect(() => {
    if (!isFetchingSpecificReport) {
      setSpecificReportResponse(dataSpecificReport);
    }
  }, [dataSpecificReport, isFetchingSpecificReport]);

  // initializing state to store search keyword for each column - to be used in run report API call
  // defs moved to context
  useEffect(() => {
    const initialColumnsSearchKeyword: Record<string, string> = {};
    dataSpecificReport?.report_fields?.forEach((field: TReportField) => {
      // initialize search keyword for each column
      initialColumnsSearchKeyword[field.field.id.toString()] = "";
    });
    setColumnsSearchKeyword(initialColumnsSearchKeyword);
  }, [dataSpecificReport?.report_fields]);

  // on change of report_fields, pre-set the groupByColumns and subGroupByColumns
  useEffect(() => {
    if (dataSpecificReport?.report_fields) {
      setSkipRunReport(true);
      // groupByColumns
      const groupByColumns: ReportTableColumn[] =
        dataSpecificReport?.report_fields
          ?.filter((field) => !!field.is_group_by && field.group_order === 1)
          .map(convertToReportTableColumn);
      setGroupByColumns(groupByColumns);
      setgroupByColumnsState(groupByColumns);

      // subGroupByColumns
      const subGroupByColumns: ReportTableColumn[] =
        dataSpecificReport?.report_fields
          ?.filter((field) => !!field.is_group_by && field.group_order > 1)
          .map(convertToReportTableColumn);
      setSubGroupByColumns(subGroupByColumns);

      // set skipRunReport to false after setting groupByColumns and subGroupByColumns
      setTimeout(() => {
        setSkipRunReport(false);
      }, 1000);
    }
  }, [dataSpecificReport?.report_fields]);

  // initializing state to store operator for each column - to be used in run report API call
  // defs moved to context
  useEffect(() => {
    const initialColumnsOperator: Record<string, string> = {};
    dataSpecificReport?.report_fields?.forEach((field: TReportField) => {
      // initialize operator for each column
      const fieldSpecficFilterOptions = getFieldSpecificFilterOptions(
        field.output_type
      );
      initialColumnsOperator[field.field.id.toString()] =
        fieldSpecficFilterOptions?.[0]; // first option as default
    });
    setColumnsOperator(initialColumnsOperator);
  }, [dataSpecificReport?.report_fields]);

  // handle `between dates` filter - prevent API call if the field with between filter has only one value
  useEffect(() => {
    if (!columnsOperator || typeof columnsOperator !== "object") return;
    let fieldWithBetweenFilterHasSingularValue = false;
    const keys = Object.keys(columnsOperator);
    if (keys.length === 0) return;
    keys.forEach((fieldId) => {
      if (
        columnsOperator[fieldId] === REPORT_FILTER_OPERATOR_TYPES.BETWEEN &&
        columnsSearchKeyword[fieldId].split(",").length === 1
      ) {
        fieldWithBetweenFilterHasSingularValue = true;
        setSkipRunReport(true);
      }
    });
  }, [columnsOperator]);

  // reset page number to 1 on any change in columnsSearchKeyword (in reportTable.tsx)
  useEffect(() => {
    if (
      // fixes the issue of API call on first load (mostly with standard reports)
      initialLoad || // instead if checking if report is initialized, we hold this only for initialLoad // reportIsUninitialized ||
      // adding reportType condition prevents issue with API call not triggering when changing column search keywords (in custom reports)
      reportType === REPORT_TYPES.STANDARD
    )
      return;
    // reset page number to 1
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        reports: {
          ...orgReportQueryParams,
          page: 1,
        },
      })
    );
    // setting skipRunReport to false to finally trigger API call
    setSkipRunReport(false);
  }, [columnsSearchKeyword]);

  // on un-mount of the component
  useEffect(() => {
    return () => {
      // reset the skipRunReport to false
      setSkipRunReport(false);
      // reset the specificReportResponse
      setSpecificReportResponse(undefined);
      // reset the runReportParams
      setRunReportParams({});
    };
  }, []);

  const memoizedRunReportParams = useMemo(() => {
    const params = {
      // params from store (updated in useEffect above)
      ...orgReportQueryParams,

      // convert TSelectboxOption[] to string of comma separated values
      ...Object.keys(orgReportQueryParams).reduce((acc, key) => {
        if (
          Array.isArray(
            orgReportQueryParams[key as keyof typeof orgReportQueryParams]
          )
        ) {
          acc[key] = (
            orgReportQueryParams[
              key as keyof typeof orgReportQueryParams
            ] as any as TSelectboxOption[]
          )
            ?.map((item: any) => item.value)
            .join(",");
        }
        return acc;
      }, {} as any),

      // if any key has value set to ALL, remove that key
      // deprecated, we are using separate values for ALL now
      // ...(Object.keys(orgReportQueryParams).reduce((acc, key) => {
      //   if (orgReportQueryParams[key as keyof typeof orgReportQueryParams] === APP_CONFIG.REPORTS.DROPDOWN_ALL_OPTION_VALUE) {
      //     acc[key] = undefined;
      //   }
      //   return acc;
      // }, {} as any)),

      // if groupByColumns is set, add report_fields as a parameter
      ...(reportType !== REPORT_TYPES.STANDARD && groupByColumns.length > 0
        ? {
            report_fields: dataSpecificReport?.report_fields?.map(
              (field: TReportField) => {
                const groupByIsActive = groupByColumns.some(
                  (col) => col.identifier === field.identifier
                );
                return {
                  field: field.field.all_field_id,
                  // aggregation param
                  ...(!!field.aggregate_function
                    ? {
                        aggregate_function: field.aggregate_function,
                        column_type: field.column_type,
                        value: field.value,
                        operator: field.operator,
                      }
                    : {}),
                  // group by param
                  ...(groupByIsActive
                    ? {
                        is_group_by: groupByColumns.some(
                          (col) => col.identifier === field.identifier
                        ),
                        group_order: groupByColumns.findIndex(
                          (col) => col.identifier === field.identifier
                        ),
                      }
                    : {}),
                };
              }
            ),
          }
        : {}),

      // for custom report - add report filters
      ...(reportType === REPORT_TYPES.STANDARD
        ? {}
        : {
            report_filters: [
              // add all saved filters from report definition
              ...(dataSpecificReport?.report_filters || []),

              // add column level search filters
              ...Object.keys(columnsSearchKeyword).reduce((acc, fieldId) => {
                if (columnsSearchKeyword[fieldId]) {
                  const fieldIdentifier =
                    dataSpecificReport?.report_fields?.find(
                      (field) => field.field.id.toString() === fieldId
                    )?.identifier;
                  // acc[`search_${key}`] = columnsSearchKeyword[key];
                  acc.push({
                    param: fieldIdentifier, // like "$asset_id"
                    operator:
                      REPORT_FILTER_OPERATOR_TYPES[
                        columnsOperator[
                          fieldId
                        ] as keyof typeof REPORT_FILTER_OPERATOR_TYPES
                      ],
                    value: columnsSearchKeyword[fieldId],
                    filter_type: REPORT_FILTER_TYPES.COLUMN,
                  });
                }
                return acc;
              }, [] as any),
            ],
          }),
    };
    return params;
  }, [
    orgReportQueryParams,
    columnsSearchKeyword,
    columnsOperator,
    //groupByColumns,
    groupByColumnsState,
  ]);

  // call setRunReportParams only when memoizedRunReportParams changes
  useEffect(() => {
    setRunReportParams(memoizedRunReportParams);
  }, [memoizedRunReportParams]);

  // report being modified state variable
  const [isReportBeingModified, setIsReportBeingModified] = useState(false);

  // fetching report data
  const {
    data: reportData,
    isFetching,
    isSuccess,
    isError,
    error: reportError,
    isUninitialized: reportIsUninitialized,
  } = (
    reportType === REPORT_TYPES.STANDARD
      ? useRunReportQuery
      : useRunCustomReportQuery
  )(
    {
      ...memoizedRunReportParams,
      ...(reportType === REPORT_TYPES.STANDARD &&
      // if time_range is present as a key
      Object.keys(memoizedRunReportParams)?.filter(
        (filterKey: string) => filterKey === "time_range"
      )?.length > 0 &&
      // and time_range value is not blank
      !!memoizedRunReportParams?.time_range
        ? // then set start_time and end_time to the standard time range
          {
            start_time: STANDARD_REPORT_TIME_RANGE_TIMESTAMP,
            end_time: STANDARD_REPORT_TIME_RANGE_TIMESTAMP,
          }
        : {}),
    },
    {
      skip:
        skipRunReport || // skipRunReport is set to true when multiple states (columnsSearchKeyword and page) are changing - prevents multiple API calls
        !memoizedRunReportParams?.id ||
        (reportType !== REPORT_TYPES.STANDARD
          ? // custom / non-standard report
            !orgReportQueryParams ||
            orgReportQueryParams.id === "" ||
            !dataSpecificReport?.report_fields ||
            isFetchingSpecificReport ||
            !groupByColumnsState // added to prevent API call (twice) when groupByColumnsState is not set
          : // standard report
            !orgReportQueryParams ||
            isFetchingSpecificReport ||
            !orgReportQueryParams.id ||
            memoizedRunReportParams?.id !== reportId) || // skip API call if reportId is not correct or stale
        // also skip if device_groups is present in params but device_groups_isactive is not set (to prevent API call with stale data)
        (reportType === REPORT_TYPES.STANDARD &&
          !!memoizedRunReportParams?.device_groups &&
          typeof memoizedRunReportParams?.[
            APP_CONFIG.REPORTS.GROUP_ASSETS_FILTER.field_name
          ] === "undefined"),
      refetchOnMountOrArgChange: true,
    }
  );

  // A 429 from run-report means an identical query is already running for this
  // user (single-flight de-dup). It is a "please wait", not a failure — so we
  // show an inline message instead of the "failed to generate" download pop-up.
  const reportErrorData = (reportError as FetchBaseQueryError)?.data as
    | { code?: string; error?: string }
    | undefined;
  const isDuplicateQueryError =
    (reportError as FetchBaseQueryError)?.status ===
      RESPONSE_CODE.TOO_MANY_REQUESTS &&
    reportErrorData?.code === "duplicate_query_in_progress";

  // The backend caches the count (60s TTL) and returns it on any page.
  // Frontend caches it locally with a filter fingerprint so stale values
  // from a previous filter set are not displayed after filter changes.
  const CACHED_COUNT_STALENESS_THRESHOLD_MS = 30_000;
  const [cachedReportCount, setCachedReportCount] = useState<{
    value: number;
    filterFingerprint: string;
    timestamp: number;
  }>({ value: 0, filterFingerprint: "", timestamp: 0 });

  const currentFilterFingerprint = JSON.stringify(
    orgReportQueryParams?.filters ?? {}
  );

  useEffect(() => {
    if (typeof reportData?.count === "number" && reportData.count > 0) {
      setCachedReportCount({
        value: reportData.count,
        filterFingerprint: currentFilterFingerprint,
        timestamp: Date.now(),
      });
    }
  }, [reportData?.count, currentFilterFingerprint]);

  // ---------------------------------------------------------------------------
  // Color mode: every row in the range in one scrolling list. Batches of
  // COLOR_MODE_BATCH_SIZE are requested by advancing `page` (the same query, so all of the
  // run-report guards above still apply) and appended here. Any other parameter change
  // (filters, sorting, column search, date range) starts the list over from page 1.
  // ---------------------------------------------------------------------------
  const [colorModeRows, setColorModeRows] = useState<any[]>([]);
  const colorModeLoadedPage = useRef(0);
  const colorModeParamsKey = useMemo(() => {
    const { page, ...rest } = memoizedRunReportParams || ({} as any);
    return JSON.stringify(rest);
  }, [memoizedRunReportParams]);
  const colorModeParamsKeyRef = useRef(colorModeParamsKey);

  const setReportPaging = (page: number, pageSize: number) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        reports: { ...orgReportQueryParams, page, page_size: pageSize },
      })
    );
  };

  const toggleColorMode = () => {
    if (!standardReportIdentifier) return;
    const on = !colorMode;
    writeColorModePreference(standardReportIdentifier, on);
    setColorModePreference((prev) => ({
      ...prev,
      [standardReportIdentifier]: on,
    }));
    setColorModeRows([]);
    colorModeLoadedPage.current = 0;
    const savedPageSize =
      (reportType && reportPageSizes[reportType]) ||
      APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE;
    setReportPaging(1, on ? COLOR_MODE_BATCH_SIZE : savedPageSize);
  };

  // parameters changed under a scrolled list: start again from the first batch
  useEffect(() => {
    if (colorModeParamsKeyRef.current === colorModeParamsKey) return;
    colorModeParamsKeyRef.current = colorModeParamsKey;
    if (!colorMode) return;
    setColorModeRows([]);
    colorModeLoadedPage.current = 0;
    if (
      orgReportQueryParams?.page !== 1 ||
      orgReportQueryParams?.page_size !== COLOR_MODE_BATCH_SIZE
    ) {
      setReportPaging(1, COLOR_MODE_BATCH_SIZE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorModeParamsKey, colorMode]);

  // append each batch as it arrives
  useEffect(() => {
    if (!colorMode || isFetching || !isSuccess || !reportData?.results) return;
    if (orgReportQueryParams?.page_size !== COLOR_MODE_BATCH_SIZE) return;
    const page = Number(orgReportQueryParams?.page) || 1;
    if (page === 1) {
      setColorModeRows(reportData.results);
      colorModeLoadedPage.current = 1;
    } else if (page === colorModeLoadedPage.current + 1) {
      setColorModeRows((prev) => [...prev, ...reportData.results]);
      colorModeLoadedPage.current = page;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorMode, reportData, isFetching, isSuccess]);

  const colorModeTotal = reportData?.count && reportData.count > 0
    ? reportData.count
    : cachedReportCount.value;
  const colorModeHasMore =
    colorMode &&
    colorModeLoadedPage.current > 0 &&
    colorModeRows.length < colorModeTotal &&
    (reportData?.results?.length ?? 0) === COLOR_MODE_BATCH_SIZE;
  const colorModeLoadingMore =
    colorMode && isFetching && Number(orgReportQueryParams?.page) > 1;

  const loadMoreColorModeRows = () => {
    if (!colorModeHasMore || isFetching) return;
    setReportPaging(colorModeLoadedPage.current + 1, COLOR_MODE_BATCH_SIZE);
  };

  // show a modal if API takes more than 60 seconds
  const [showErrorModal, setShowErrorModal] = useState(false);
  useEffect(() => {
    let timeout: any;
    if (isFetching) {
      timeout = setTimeout(
        () => {
          setShowErrorModal(true);
        },
        1000 * 60 * 3
      );
    } else {
      setShowErrorModal(false);
      clearTimeout(timeout);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [isFetching]);
  // show a modal if API returns an error
  useEffect(() => {
    if (isError) {
      if (
        (reportError as FetchBaseQueryError)?.status ===
        RESPONSE_CODE.UNAUTHORIZED
      ) {
        handleAuthErrorCode(reportError as FetchBaseQueryError);
      } else if (isDuplicateQueryError) {
        // Duplicate in-flight query (429): show the inline message only,
        // never the "failed to generate" download pop-up.
        setShowErrorModal(false);
      } else {
        setShowErrorModal(true);
      }
    }
  }, [isError]);

  const [intervalCount, setIntervalCount] = useState(0);
  const [processId, setProcessId] = useState<number | null>(null);
  const intervalRef = useRef<any>(null);
  const [initReportDownloadProcessAPITrigger] =
    useLazyInitReportDownloadQuery();
  // The job whose download this page is still waiting for, and whether its one final
  // status check has been made; see shouldRunFinalCheck.
  const activeProcessRef = useRef<number | null>(null);
  const finalCheckRef = useRef(false);
  const { data: processStatusData, refetch: refetchProcessStatus } =
    useGetReportProcessStatusQuery(processId as number, {
      skip: !processId,
      pollingInterval: APP_CONFIG.API_POLLING_INTERVALS.UPLOADS_DOWNLOADS,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    });

  const clearProcessingData = () => {
    activeProcessRef.current = null;
    finalCheckRef.current = false;
    setProcessId(null);
    window.clearInterval(intervalRef.current);
    intervalRef.current = null;
    setIntervalCount(0);
  };

  useEffect(() => {
    if (
      processStatusData?.status === SCHEDULER_PROCESS_STATUS.COMPLETED &&
      intervalCount > 0
    ) {
      console.log("on completion", processStatusData);
      downloadReport(
        processStatusData?.output_files[0]?.file,
        processStatusData?.output_files[0]?.name
      );
      clearProcessingData();
      setModalType("success");
    } else if (processStatusData?.status === SCHEDULER_PROCESS_STATUS.FAILED) {
      clearProcessingData();
      setModalType("error");
    } else if (
      shouldRunFinalCheck(
        processStatusData?.status,
        intervalCount,
        finalCheckRef.current
      )
    ) {
      // Ask once more before giving up: the last poll may predate the job finishing. A
      // finished or failed answer lands in processStatusData and is handled above.
      finalCheckRef.current = true;
      const checkedProcess = activeProcessRef.current;
      const giveUp = () => {
        if (activeProcessRef.current !== checkedProcess) return;
        clearProcessingData();
        setModalType("longProcess");
      };
      refetchProcessStatus()
        .unwrap()
        .then((latest) => {
          if (shouldGiveUpAfterFinalCheck(latest?.status)) giveUp();
        })
        .catch(giveUp);
    }
  }, [intervalCount, processStatusData, refetchProcessStatus, tReports]);

  const startLongProcessInterval = () => {
    if (!intervalRef) {
      return;
    }
    intervalRef.current = setInterval(() => {
      // incement the progress
      setIntervalCount((count) => count + 1);
    }, 1000);
  };

  const onDownloadReportClick = (format: string) => {
    setModalType("processing");
    const fieldData = dataSpecificReport?.report_fields
      ?.map((field: TReportField) => {
        return field.field.id.toString();
      })
      .filter((_, index) => columnsVisibility[index]?.show);
    const downloadQueryParam = { ...listingQueryParams.reports };
    delete (downloadQueryParam as any)["page"];
    delete (downloadQueryParam as any)["page_size"];

    // convert TSelectboxOption[] (e.g. multi-select geozone_id) to string of comma separated values, as the run report call does
    Object.keys(downloadQueryParam).forEach((key) => {
      const value = (downloadQueryParam as any)[key];
      if (Array.isArray(value))
        (downloadQueryParam as any)[key] = (value as TSelectboxOption[])
          .map((item: any) => item.value)
          .join(",");
    });

    // if start_time and end_time are not present, add them
    if (!downloadQueryParam.start_time)
      downloadQueryParam.start_time = STANDARD_REPORT_TIME_RANGE_TIMESTAMP;
    if (!downloadQueryParam.end_time)
      downloadQueryParam.end_time = STANDARD_REPORT_TIME_RANGE_TIMESTAMP;

    initReportDownloadProcessAPITrigger({
      ...downloadQueryParam,
      fields: fieldData ? fieldData : [],
      response_format: format,
    })
      .unwrap()
      .then((response: RunReportDownloadProcessReponse) => {
        if (response && response.id) {
          activeProcessRef.current = response.id;
          finalCheckRef.current = false;
          setProcessId(response.id);
          startLongProcessInterval();
        }
      })
      .catch(() => {});
  };

  const getModalHeading: string | null = useMemo(() => {
    switch (modalType) {
      case "upload":
      case "processing":
      case "longProcess":
        return tToast("processing");
      case "success":
        return tToast("success");
      case "error":
        return tToast("error");
      default:
        return "";
    }
  }, [modalType, tToast]);

  const onModalActionClick = () => {
    clearProcessingData();
    setModalType(null);
  };

  /**
   * Get column data for table and edit column modal
   */
  const allColumns: ReportTableColumn[] = useMemo(() => {
    if (!dataSpecificReport?.report_fields) return [];

    // Prepare column data for table
    const columnData: ReportTableColumn[] =
      dataSpecificReport?.report_fields?.map(convertToReportTableColumn) || [];
    // removed filtration 01/17/2025 as this is causing incorrect fields update via PATCH API call
    // .filter((_, index) => columnsVisibility[index]?.show) || []; // Filter out columns that are selected

    // Extra column for Sr. No.
    const srNumColumn = {
      header: "#",
      id: "index",
      identifier: "index",
    } as ReportTableIndexColumn;
    return [srNumColumn, ...columnData];
  }, [dataSpecificReport?.report_fields, columnsVisibility]);

  // In color mode rows accumulate batch by batch; converted rows are cached by row object so
  // each new batch converts only its own rows. A new cache whenever the conversion inputs change.
  const colorModeRowCache = useMemo(
    () => new WeakMap<object, any[]>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allColumns, urlParamReportFilters, dataSpecificReport, colorMode]
  );
  const tableSourceRows: any[] | undefined = colorMode
    ? colorModeRows
    : reportData?.results;

  const tableData = useMemo(() => {
    if (
      tableSourceRows?.length &&
      allColumns?.length &&
      dataSpecificReport?.report_fields?.length
    ) {
      return tableSourceRows.map((item: any, index: number) => {
        const cachedRow = colorMode ? colorModeRowCache.get(item) : undefined;
        if (cachedRow) return cachedRow;
        // Sort data in order of viewable columns and retrieve only viewable column data from item
        let viewableData = allColumns.map((col) => {
          if (col.identifier === "index")
            return colorMode
              ? index + 1
              : orgReportQueryParams.page_size * (orgReportQueryParams.page - 1) +
                  index +
                  1;
          const this_field_config = dataSpecificReport?.report_fields?.find(
            (field) =>
              reportType === REPORT_TYPES.STANDARD
                ? field?.id === Number(col?.id)
                : field?.identifier === col?.identifier //field.identifier === col.identifier
          );
          const output_type = this_field_config?.output_type;
          const convertAsWholeNumber =
            this_field_config?.convert_to_whole_number;
          // the following change from `item[col.identifier]` to `item?.[col.id as keyof typeof item]` was needed after
          // the last API change to run report response that was made for dashboard widgets
          const cellData =
            item?.[
              (reportType === REPORT_TYPES.STANDARD
                ? col.id
                : col.identifier) as keyof typeof item
            ]; // item[col.identifier];

          // alignment for number fields
          let alignment = "left";
          const all_number_output_types =
            REPORT_FIELD_OUTPUT_TYPES_NUMBER_ALIGNMENT;
          if (
            Object.values(all_number_output_types).includes(output_type as any)
          ) {
            alignment = APP_CONFIG.REPORTS.NUMBER_CELLDATA_ALIGNMENT;
          }

          // unit conversions
          let convertedCellData = {
            finalData: cellData,
            isConverted: false,
          };
          try {
            // skip conversion if aggregate function is COUNT
            if (
              this_field_config?.aggregate_function !== AGGREGATE_FUNCTION.COUNT
            ) {
              convertedCellData = convertToUnit(
                output_type as REPORT_FIELD_OUTPUT_TYPES,
                cellData,
                convertAsWholeNumber
              );
            }
          } catch (error) {
            if (APP_CONFIG.DEBUG.REPORTS) {
              console.error("ERROR IN UNIT CONVERSION", error);
              console.error("cellData", cellData);
              console.error("output_type", output_type);
            }
          }
          return (
            <DisplayConvertedValue
              alignment={alignment as any}
              convertedCellData={convertedCellData}
              originalData={cellData}
              outputType={
                colorMode ? (output_type as REPORT_FIELD_OUTPUT_TYPES) : undefined
              }
            />
          );
        });
        if (colorMode) colorModeRowCache.set(item, viewableData);
        return viewableData;
      });
    }
    return [];
  }, [
    tableSourceRows,
    colorMode,
    colorModeRowCache,
    allColumns,
    orgReportQueryParams,
    urlParamReportFilters,
    isFetching,
    isSuccess,
    dataSpecificReport,
  ]);

  const {
    createModifyCustomReportWithFilters,
    navigateOnReportOptionsChange,
    refetchCustomReportListToStore,
  } = useReportUtils();

  const handleUpdateReportOptions: TReportOptionsSubmitCallback = (values) => {
    if (APP_CONFIG.DEBUG.REPORTS) console.log("VALUES RECEIVED", values);

    // add changes to basic filters but also retain non-basic filters
    const allReportFilters = [
      // include new basic filters from `values`
      ...(!!values.reportFilters ? values.reportFilters : []),
      // include all other existing filters except basic filters
      ...(dataSpecificReport?.report_filters?.filter(
        (filter) => filter.filter_type !== REPORT_FILTER_TYPES.BASIC
      ) || []),
    ];

    // if report is custom, update the report with new filters
    if (
      reportType === REPORT_TYPES.NEW_CUSTOM ||
      reportType === REPORT_TYPES.CUSTOM
    ) {
      // CUSTOM - update the report with new filters
      setIsReportBeingModified(true);
      createModifyCustomReportWithFilters({
        modifyReportId: parseInt(reportId as string),
        reportName: dataSpecificReport?.name ?? "",
        reportType: REPORT_TYPES.CUSTOM,
        category: parseInt(categoryId as string),
        filterValues: values.filterValues,
        reportFilters: allReportFilters,
        filtersUpdateRequired: true,
        finallyCallback: () => {
          // reset the state variable
          setIsReportBeingModified(false);
        },
      } as TModifyCustomReportCallbackProps);
    } else {
      // STANDARD / SHARED - navigate to the report with updated filters
      const { filterValues, reportFilters } = values;
      dispatch(
        setModalsData({ ...modalsState, showUpdateReportOptions: false })
      );
      navigateOnReportOptionsChange({
        filterValues: filterValues,
        reportType: reportType as keyof typeof REPORT_TYPES,
        category: parseInt(categoryId as string),
        reportId: parseInt(reportId as string),
        navigate,
        reportFilters,
      });
    }
  };

  const [deleteCustomReport] = useDeleteCustomReportMutation();

  const handleDeleteCustomReport = useCallback(() => {
    deleteCustomReport(parseInt(reportId as string))
      .unwrap()
      .then(() => {
        // invalidate dashboard list as well because deleting a custom report also deletes the associated custom widget
        dispatch({
          type: `${reducerPath}/invalidateTags`,
          payload: [OverviewApiTags.CUSTOM_WIDGET_DELETED],
        });

        // call getReportsByCatType API to get the latest reports
        refetchCustomReportListToStore(() => {
          // on success, show toast and navigate to reports list
          toast.success(tReports("toast.delete_success"));
          navigate(routeUrls.dashboardChildren.reportsChildren.select_a_type);
        });
      })
      .catch(() => {
        toast.error(tReports("toast.delete_error"));
      });
  }, [reportId]);

  const onSortClick = (orderBy: string | undefined) => {
    if (!orderBy) {
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          reports: {
            ...orgReportQueryParams,
            ordering: "",
          },
        })
      );
    } else {
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          reports: {
            ...orgReportQueryParams,
            ordering: orderBy,
          },
        })
      );
    }
  };

  const [isCopyModify, setIsCopyModify] = useState(false);

  const handleEditReportClick = () => {
    // set the report id to be modified
    // setSelectedReportId(parseInt(reportId as string));
    setIsCopyModify(false);
    if (!!dataSpecificReport?.linked_standard_report) {
      setSelectedReportId(dataSpecificReport?.linked_standard_report);
    }
    // show the next step
    dispatch(
      setModalsData({
        ...modalsState,
        // enable 2
        showModalNewCustomReportStep2: true,
        // disable all other modals
        showModalNewCustomReportStep1: false,
        showModalNewCustomReportStep3: false,
        showModalMakeCopy: false,
      })
    );
  };

  const handleEditCopyReportClick = () => {
    handleEditReportClick();
    setIsCopyModify(true);
  };

  const handleCopyReportClick = () => {
    dispatch(
      setModalsData({
        ...modalsState,
        showModalMakeCopy: true,
        showModalNewCustomReportStep2: false,
        showModalNewCustomReportStep3: false,
      })
    );
  };

  const handleCreateCustomClick = () => {
    dispatch(
      setModalsData({
        ...modalsState,
        showModalMakeCopy: false,
        showModalMakeCustomReport: true,
        showModalNewCustomReportStep2: false,
        showModalNewCustomReportStep3: false,
      })
    );
  };

  const handleCreateCustomReport = (reportName: string) => {
    createModifyCustomReportWithFilters({
      reportId: parseInt(reportId as string),
      reportName: reportName,
      reportType: REPORT_TYPES.CUSTOM,
      category: parseInt(categoryId as string),
      selectedFields: dataSpecificReport?.report_fields.map((field) => {
        return {
          field: Number(field.id),
          field_title: field.name,
        };
      }),
      reportFilters: dataSpecificReport?.report_filters,
      filterValues: filteredAPIParamJson,
      navigate,
      //filtersUpdateRequired: true,
      finallyCallback: () => {
        //setIsReportBeingModified(false);
        dispatch(
          setModalsData({
            ...modalsState,
            showModalMakeCustomReport: false,
          })
        );
      },
    } as TModifyCustomReportCallbackProps);
  };

  const handleApplyReportColumnFilter = (
    identifier: string,
    fieldOutputType: REPORT_FIELD_OUTPUT_TYPES,
    selectedValues: string[]
  ) => {
    const updatedReportFilters = [
      // report filters excluding column filters
      ...(dataSpecificReport?.report_filters?.filter(
        (filter) => filter.filter_type !== REPORT_FILTER_TYPES.COLUMN
      ) || []),
      // column filters that were already saved
      ...(dataSpecificReport?.report_filters
        ?.filter((filter) => filter.filter_type === REPORT_FILTER_TYPES.COLUMN)
        ?.map(
          (field) =>
            ({
              filter_type: REPORT_FILTER_TYPES.COLUMN,
              operator: field.operator,
              param: field.param,
              value:
                field.param === identifier
                  ? selectedValues.join(",")
                  : field.value,
            }) as TReportFilterRequired
        ) || []),
    ];
    const newlyAddedReportFilters = [
      // add new column filters
      ...(dataSpecificReport?.report_filters?.filter(
        (filter) =>
          filter.filter_type === REPORT_FILTER_TYPES.COLUMN &&
          filter.param === identifier
      ).length === 0
        ? [
            {
              filter_type: REPORT_FILTER_TYPES.COLUMN,
              operator: REPORT_FILTER_OPERATOR_TYPES.IN,
              param: identifier,
              value: selectedValues.join(","),
            },
          ]
        : []),
    ];
    const paramReportFilters = [
      ...updatedReportFilters,
      ...newlyAddedReportFilters,
    ]?.filter(
      (filter) =>
        filter.filter_type !== REPORT_FILTER_TYPES.BASIC
          ? filter.value !== "" // for non basic filters, ensure that the value is not empty
          : true // for basic filters, allow empty values (to keep initially provided BE values and show correct 'basic' report options - like empty device_id when groups are selected to show filter_by dropdown)
    );
    if (APP_CONFIG.DEBUG.REPORTS) {
      console.log("[handler] updatedReportFilters", updatedReportFilters);
      console.log("[handler] newlyAddedReportFilters", newlyAddedReportFilters);
      console.log("[handler] paramReportFilters", paramReportFilters);
    }
    // update the (filter_type = 2) report filters
    setIsReportBeingModified(true);
    createModifyCustomReportWithFilters({
      modifyReportId: parseInt(reportId as string),
      reportType: REPORT_TYPES.CUSTOM,
      category: parseInt(categoryId as string),
      filterValues: {
        ...filteredAPIParamJson,
        // moved this nonce to `pages/reports/util.ts` to ensure it gets added/updated with every change
        // nonce: Date.now() // added nonce to force report re-fetch
      },
      reportFilters: paramReportFilters,
      filtersUpdateRequired: true,
      finallyCallback: () => {
        setIsReportBeingModified(false);
        // switch to page 1
        dispatch(
          setListingQueryParams({
            ...listingQueryParams,
            reports: {
              ...orgReportQueryParams,
              page: 1,
            },
          })
        );
      },
    } as TModifyCustomReportCallbackProps);
  };

  // fetch all fields for the report
  const standaredReportFieldsQueryParams = {
    page: 1,
    page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
    search: "",
    report: parseInt(reportId as string) || 0,
    is_custom: true,
  };
  const { data: dataReportAllFields, isFetching: isFetchingReportAllFields } =
    useGetReportAllFieldsQuery(standaredReportFieldsQueryParams, {
      skip: standaredReportFieldsQueryParams.report === 0,
    });

  // Column Calculation
  const handleApplyReportColumnCalculation = (
    fieldId: string,
    aggregate_function?: string,
    column_type?: number,
    value?: number,
    operator?: string
  ) => {
    const selectedField = {
      id: fieldId,
      aggregate_function,
      column_type,
      value,
      operator,
    };

    const allFields = dataSpecificReport?.report_fields.map(
      (reportFieldItem) => {
        const field = dataReportAllFields?.results.find(
          (field) => field.identifier === reportFieldItem.identifier
        ) as StandardReportFieldResponse;
        return {
          ...field,
          ...reportFieldItem,
          id: field?.id.toString(), // TODO: this can be changed to `all_field_id`
          name: field?.name,
          aggregate_function: reportFieldItem?.aggregate_function || undefined,
          column_type:
            reportFieldItem?.column_type &&
            reportFieldItem.aggregate_function !== ""
              ? reportFieldItem?.column_type
              : undefined,
          value: reportFieldItem?.value || undefined,
          operator: reportFieldItem?.operator || undefined,
        };
      }
    );

    const selectedFields = (allFields || []).map((field) => {
      if (field.id === fieldId) {
        return {
          ...selectedField,
          name: field.name, // ensuring that the name is not lost
        };
      }
      return field;
    });
    setIsReportBeingModified(true);
    createModifyCustomReportWithFilters({
      modifyReportId: parseInt(reportId as string),
      reportType: REPORT_TYPES.CUSTOM,
      category: parseInt(categoryId as string),
      selectedFields: selectedFields.map((field) => {
        return {
          ...field,
          field: field.id,
          field_title: field.name, // needed for column visibility
        } as any as ModifyCustomReportPayloadField;
      }),

      finallyCallback: () => {
        setIsReportBeingModified(false);
        // switch to page 1
        dispatch(
          setListingQueryParams({
            ...listingQueryParams,
            reports: {
              ...orgReportQueryParams,
              page: 1,
            },
          })
        );
        // refetch the report data
        refetchDataSpecificReport();
      },
    } as TModifyCustomReportCallbackProps);
  };

  // Advanced Filter
  const handleApplyReportAdvancedFilter = (
    identifier: string,
    value: string,
    filters: {
      identifier: string;
      fieldOutputType: REPORT_FIELD_OUTPUT_TYPES;
      selectedValues: string[];
      operator: string;
      id: string;
    }[]
  ) => {
    // update the (filter_type = 2) report filters
    setIsReportBeingModified(true);
    createModifyCustomReportWithFilters({
      modifyReportId: parseInt(reportId as string),
      reportType: REPORT_TYPES.CUSTOM,
      category: parseInt(categoryId as string),
      filterValues: {
        ...filteredAPIParamJson,
        // moved this nonce to `pages/reports/util.ts` to ensure it gets added/updated with every change
        // nonce: Date.now() // added nonce to force report re-fetch
      },

      reportFilters: [
        ...(dataSpecificReport?.report_filters?.filter(
          (filter) => filter.filter_type == REPORT_FILTER_TYPES.BASIC
        ) || []),
        ...(dataSpecificReport?.report_filters
          ?.filter(
            (filter) => filter.filter_type === REPORT_FILTER_TYPES.COLUMN
          )
          ?.map(
            (field) =>
              ({
                filter_type: REPORT_FILTER_TYPES.COLUMN,
                operator: field.operator,
                param: field.param,
                value: field.value,
              }) as TReportFilterRequired
          ) || []),
        {
          filter_type: REPORT_FILTER_TYPES.ADVANCED,
          operator: REPORT_FILTER_OPERATOR_TYPES.EQUAL,
          param: identifier,
          value: value,
          report_advance_filters: filters.map((filter, i) => ({
            sequence: i + 1,
            field: filter.id, // to be done
            // param: filter.identifier,
            value: filter.selectedValues,
            operator: filter.operator, // REPORT_FILTER_OPERATOR_TYPES.IN,
          })),
        },
      ],

      finallyCallback: (requestSuccessful) => {
        setIsReportBeingModified(false);
        // switch to page 1
        dispatch(
          setListingQueryParams({
            ...listingQueryParams,
            reports: {
              ...orgReportQueryParams,
              page: 1,
            },
          })
        );
        // close popup
        if (!requestSuccessful) return;
        dispatch(
          setModalsData({
            ...modalsState,
            showAdvanceFilterPopup: false,
          })
        );
      },
    } as TModifyCustomReportCallbackProps);
  };

  const handleResetReportAdvancedFilter = () => {
    // update the (filter_type = 2) report filters
    setIsReportBeingModified(true);
    createModifyCustomReportWithFilters({
      modifyReportId: parseInt(reportId as string),
      reportType: REPORT_TYPES.CUSTOM,
      category: parseInt(categoryId as string),
      filterValues: {
        ...filteredAPIParamJson,
      },

      reportFilters: [
        ...(dataSpecificReport?.report_filters?.filter(
          (filter) => filter.filter_type == REPORT_FILTER_TYPES.BASIC
        ) || []),
        ...(dataSpecificReport?.report_filters
          ?.filter(
            (filter) => filter.filter_type === REPORT_FILTER_TYPES.COLUMN
          )
          ?.map(
            (field) =>
              ({
                filter_type: REPORT_FILTER_TYPES.COLUMN,
                operator: field.operator,
                param: field.param,
                value: field.value,
              }) as TReportFilterRequired
          ) || []),
      ],

      finallyCallback: () => {
        setIsReportBeingModified(false);
        // switch to page 1
        dispatch(
          setListingQueryParams({
            ...listingQueryParams,
            reports: {
              ...orgReportQueryParams,
              page: 1,
            },
          })
        );
      },
    } as TModifyCustomReportCallbackProps);
  };

  // group by drag and drop
  const onDragEnd = ({ source, destination }: DropResult) => {
    const draggedColumn = allColumns.find(
      (col) =>
        String(col.id) ===
        source.droppableId.replace(DROPPABLE_ID_PREFIX_REPORT_COLUMN, "")
    );

    if (!destination || !draggedColumn) return;

    if (destination.droppableId === REPORT_GROUP_BY_LEVEL_SLUGS.GROUP_BY) {
      // if draggedColumn is already in groupByColumns, do nothing
      if (
        groupByColumns.find(
          (col) => String(col.id) === String(draggedColumn.id)
        )
      )
        return;

      // as we are not using multiple group by columns, we can safely skip the report run
      setSkipRunReport(true);

      // if draggedColumn is already in subGroupByColumns, remove it from there
      const newSubGroupByColumns = subGroupByColumns.filter(
        (col) => col.id !== draggedColumn.id
      );
      setSubGroupByColumns(newSubGroupByColumns);

      // add the dropped item to the list of group by columns
      setGroupByColumns([draggedColumn]);
    } else if (
      destination.droppableId === REPORT_GROUP_BY_LEVEL_SLUGS.SUB_GROUP_BY
    ) {
      // if draggedColumn is already in subGroupByColumns, do nothing, or
      // if draggedColumn is already in groupByColumns, do nothing
      if (
        subGroupByColumns.find(
          (col) => String(col.id) === String(draggedColumn.id)
        ) ||
        groupByColumns.find(
          (col) => String(col.id) === String(draggedColumn.id)
        )
      )
        return;

      // as we are not using multiple group by columns, we can safely skip the report run
      setSkipRunReport(true);

      // add the dropped item to the list of sub group by columns
      const newSubGroupByColumns = Array.from(subGroupByColumns);
      newSubGroupByColumns.push(draggedColumn);
      setSubGroupByColumns(newSubGroupByColumns);
    }
    setSkipRunReport(false);
  };

  const [lastPreparedSelectedFields, setLastPreparedSelectedFields] = useState<
    ModifyCustomReportPayloadField[]
  >([]);
  // on debounced update of groupByColumns or subGroupByColumns, update the report fields column order
  const debouncedModifyCustomReportFields = useDebouncedCallback(
    (newGroupByColumns, newSubGroupByColumns) => {
      if (debugComponent) {
        console.log("debouncedModifyCustomReportFields ===========");
        console.log("newGroupByColumns", newGroupByColumns);
        console.log("newSubGroupByColumns", newSubGroupByColumns);
      }
      // prepare the selected fields parameter
      let preparedSelectedFields = (
        allColumns.filter((col) => col.id !== "index") || []
      ).map((column) => {
        const thisFieldIsSelectedAsGroupBy = (
          newGroupByColumns as ReportTableColumn[]
        ).find((col) => col.id === column.id);
        const thisFieldIsSelectedAsSubGroupBy = (
          newSubGroupByColumns as ReportTableColumn[]
        ).find((col) => col.id === column.id);
        const fieldIndexInSubGroupBy = (
          newSubGroupByColumns as ReportTableColumn[]
        ).findIndex((col) => col.id === column.id);
        return {
          field: Number(column.all_field_id),
          field_title: column.header, // TODO: need to check
          ...(column.aggregation_applied && !!column.field_object
            ? {
                aggregate_function: column.field_object?.aggregate_function,
                column_type: column.field_object?.column_type,
                value: column.field_object?.value,
                operator: column.field_object?.operator,
              }
            : {}),
          is_group_by:
            !!(newGroupByColumns as ReportTableColumn[]).find(
              (col) => col.id === column.id
            ) ||
            !!(newSubGroupByColumns as ReportTableColumn[]).find(
              (col) => col.id === column.id
            ),
          group_order: thisFieldIsSelectedAsGroupBy
            ? 1
            : thisFieldIsSelectedAsSubGroupBy
              ? fieldIndexInSubGroupBy + 2
              : undefined,
        } as ModifyCustomReportPayloadField;
      });
      // if the newGroupByColumns or newSubGroupByColumns are not empty, sort the preparedSelectedFields by group_order
      if (newGroupByColumns.length > 0 || newSubGroupByColumns.length > 0) {
        console.log("sorting preparedSelectedFields");
        preparedSelectedFields = preparedSelectedFields.sort(
          (a, b) => (a.group_order || 999) - (b.group_order || 999)
        );
      }
      setLastPreparedSelectedFields(preparedSelectedFields);
      console.log({
        selectedFields: preparedSelectedFields,
      });
      // update the report fields
      setIsReportBeingModified(true);
      if (reportType !== REPORT_TYPES.STANDARD) {
        // check if prepared selected fields are same as the existing fields and if so, skip the update
        // check method 1 - using arrayEquals - deprecated
        // const existingFields = dataSpecificReport?.report_fields || [];
        // const existingFieldsIdentifiers = existingFields.map((field) =>
        //   String(field.field.all_field_id)
        // );
        // const preparedFieldsIdentifiers = preparedSelectedFields.map((field) =>
        //   String(field.field)
        // );
        // const fieldsAreSame = arraysEqual(
        //   existingFieldsIdentifiers,
        //   preparedFieldsIdentifiers
        // );
        // if (APP_CONFIG.DEBUG.REPORTS) {
        //   console.log("existingFieldsIdentifiers", existingFieldsIdentifiers);
        //   console.log("preparedFieldsIdentifiers", preparedFieldsIdentifiers);
        // }
        // if (fieldsAreSame) {
        //   if (APP_CONFIG.DEBUG.REPORTS)
        //     console.log("same fields, skipping update");
        //   // setIsReportBeingModified(false);
        //   // return;
        // } else {
        //   if (APP_CONFIG.DEBUG.REPORTS)
        //     console.log("fields are different, updating report");
        // }
        // check method 2 - using deepEquals
        // if (lastPreparedSelectedFields.length === 0) {
        //   if (APP_CONFIG.DEBUG.REPORTS)
        //     console.log("====== last prepared is null, skipping update");
        //   setIsReportBeingModified(false);
        //   return;
        // }
        // if (deepEquals(preparedSelectedFields, lastPreparedSelectedFields)) {
        //   if (APP_CONFIG.DEBUG.REPORTS)
        //     console.log("====== same fields, skipping update");
        //   setIsReportBeingModified(false);
        //   return;
        // }
        // if (APP_CONFIG.DEBUG.REPORTS) {
        //   console.log("====== fields are different, updating report");
        //   console.log("preparedSelectedFields", preparedSelectedFields);
        //   console.log("lastPreparedSelectedFields", lastPreparedSelectedFields);
        // }
        // check method 3 - cancelling calls to this function for first 10 seconds using initialLoad

        // CUSTOM - update the report with new filters
        createModifyCustomReportWithFilters({
          modifyReportId: parseInt(reportId as string),
          selectedFields: preparedSelectedFields,
          // (
          //   allColumns.filter((col) => col.id !== "index") || []
          // ).map((column) => {
          //   return {
          //     field: Number(column.id),
          //     // is_group_by?: boolean;
          //     // group_order?: number;
          //   } as ModifyCustomReportPayloadField;
          // }),
          reportType: reportType as REPORT_TYPES,
          category: parseInt(categoryId as string),
          filterValues: {
            ...filteredAPIParamJson,
            // moved this nonce to `pages/reports/util.ts` to ensure it gets added/updated with every change
            nonce: Date.now(), // added nonce to force report re-fetch
          },
          reportFilters: dataSpecificReport?.report_filters || [],
          filtersUpdateRequired: false,
          finallyCallback: () => {
            setIsReportBeingModified(false);
          },
        } as TModifyCustomReportCallbackProps);
      } else {
        // STANDARD / SHARED - navigate to the report with updated filters
        setIsReportBeingModified(false);
      }
    },
    500
  );

  const prevGroupByColumns = useRef(groupByColumns);
  const prevSubGroupByColumns = useRef(subGroupByColumns);
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (skipRunReport) return;

    // cancel any requests for first 10 seconds of initial load
    if (initialLoad) {
      setTimeout(() => {
        setInitialLoad(false);
      }, 10 * 1000);
      return;
    }
    if (
      prevGroupByColumns.current !== groupByColumns ||
      prevSubGroupByColumns.current !== subGroupByColumns
    ) {
      debouncedModifyCustomReportFields(groupByColumns, subGroupByColumns);
      prevGroupByColumns.current = groupByColumns;
      prevSubGroupByColumns.current = subGroupByColumns;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupByColumns, subGroupByColumns]);

  // const reportSlug = `/event-detail/${thisUserOrganizationId}/${entityId}/${unixTimeFrom}/${unixTimeTo}`;
  return (
    <DragDropContext
      onDragEnd={onDragEnd}
      dragHandleUsageInstructions="Use the handle to drag"
    >
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && debugComponent && (
        <div className="flex gap-4 text-[8px]">
          <pre>
            listingQueryParams.reports:
            <br />
            {JSON.stringify(listingQueryParams.reports, null, 2)}
          </pre>
          <pre>
            orgReportQueryParams:
            <br />
            {JSON.stringify(orgReportQueryParams, null, 2)}
          </pre>
          <pre>
            reportId:
            <br />
            {JSON.stringify(reportId, null, 2)}
          </pre>
          <pre>
            allColumns (truncated):
            <br />
            {JSON.stringify(
              allColumns.map((col) => col.identifier),
              null,
              2
            )}
          </pre>
          <pre>
            filteredAPIParams:
            <br />
            {JSON.stringify(filteredAPIParams, null, 2)}
          </pre>
        </div>
      )}
      {/* Header Bar */}
      <HeaderBar
        preTitle={
          reportType === REPORT_TYPES.SHARED_WITH_ME
            ? undefined
            : reportType === REPORT_TYPES.STANDARD
              ? tReports(`titles.report_category_${categoryId}`)
              : tReports(`titles.type_${reportType}`)
        }
        title={
          categoryId === REPORT_CATEGORIES.OTHERS_DASHBOARD_WIDGETS
            ? tReports(`titles.type_other`)
            : !!dataSpecificReport?.name
              ? dataSpecificReport?.name
              : !!reportsByType[reportType as TReportTypes] &&
                  Array.isArray(reportsByType[reportType as TReportTypes])
                ? ((reportsByType[reportType as TReportTypes] || [])?.find(
                    (report) => `${report.id}` === reportId
                  )?.name ?? "")
                : ""
        }
        subTitle={
          reportType === REPORT_TYPES.SHARED_WITH_ME ? (
            <div className="flex gap-1 items-center text-sm -mt-1">
              <div className="text-slate-400">
                {tReports(`titles.shared_by`)}
              </div>
              <div className="font-semibold">{sharedBy || ""}</div>
            </div>
          ) : undefined
        }
        reportOnDisplay={
          !!reportsByType[reportType as TReportTypes] &&
          !!reportsByType[reportType as TReportTypes]?.[0]?.id
            ? (reportsByType[reportType as TReportTypes] || [])?.find(
                (report) => `${report.id}` === reportId
              )
            : undefined
        }
        rightComponent={
          <div className="flex items-center gap-4 shrink-0 ms-4">
            {/* Edit Button and Modal - converts Standard to a new Custom Report */}
            {/* disabled 11/21/2024 - because we will now have new flow for creating custom reports */}
            {/* <AdminFormFieldSubmit
              label={tMain("edit")}
              variant="primary"
              type="button"
              onClick={() => {
                reportType === REPORT_TYPES.STANDARD
                  ? dispatch(setModalsData({ ...modalsState, showDuplicateReportModal: true }))
                  : dispatch(setModalsData({ ...modalsState, showAddEditReportModal: true }))
              }}
            />
            <ModalDuplicateReport
              reportType={REPORT_TYPES.CUSTOM}
              category={parseInt(categoryId as string)}
              reportId={parseInt(reportId as string)}
              reportField={dataSpecificReport?.report_fields ?? []}
              filterValues={filteredAPIParamJson ?? {}}
            />
            {dataSpecificReport && !isFetchingSpecificReport &&
              <ModalAddEditReport
                editSpecificReport={dataSpecificReport}
                savedColumns={columns?.map((col) => col.name)}
                reportId={parseInt(reportId as string)}
              />
            } */}
            {/* Make a copy Custom Report Button */}
            {(reportType === REPORT_TYPES.STANDARD ||
              reportType === REPORT_TYPES.CUSTOM ||
              reportType === REPORT_TYPES.NEW_CUSTOM ||
              reportType === REPORT_TYPES.SHARED_WITH_ME) && (
              <>
                <AdminFormFieldSubmit
                  label={
                    reportType === REPORT_TYPES.STANDARD
                      ? tReports("create_custom_button")
                      : tReports("make_copy_button")
                  }
                  variant="primary"
                  type="button"
                  onClick={() => {
                    reportType !== REPORT_TYPES.STANDARD
                      ? handleCopyReportClick()
                      : handleCreateCustomClick();
                  }}
                />
                {modalsState.showModalMakeCopy && (
                  <ModalMakeCopy
                    reportId={reportId}
                    handleEdit={handleEditCopyReportClick}
                  />
                )}
                {modalsState?.showModalMakeCustomReport && (
                  <ModalMakeCustomReport
                    handleCreateCustomReport={handleCreateCustomReport}
                  />
                )}
              </>
            )}
            {/* Edit Custom Report Button */}
            {(reportType === REPORT_TYPES.CUSTOM ||
              reportType === REPORT_TYPES.NEW_CUSTOM ||
              reportType === REPORT_TYPES.SHARED_WITH_ME) && (
              <>
                {reportType !== REPORT_TYPES.SHARED_WITH_ME && (
                  <AdminFormFieldSubmit
                    label={tMain("edit")}
                    variant="primary"
                    type="button"
                    onClick={handleEditReportClick}
                  />
                )}
                {modalsState?.showModalNewCustomReportStep2 &&
                  !!dataSpecificReport && (
                    <ModalNewCustomReportsStep2
                      modifyReport={dataSpecificReport}
                      isCopyModify={isCopyModify}
                    />
                  )}
                {modalsState?.showModalNewCustomReportStep3 &&
                  !!selectedReportId && (
                    <ModalReportOptions
                      filterValuesForPrefill={
                        // paramJson
                        // in the case of custom reports we ignore paramJson and use the filterValues from the report
                        paramJsonForReportOptions
                      }
                      reportType={REPORT_TYPES.NEW_CUSTOM}
                      reportId={selectedReportId}
                      modalVariant={"showModalNewCustomReportStep3"}
                      translationKeyPrefix={
                        "reports.createCustomReport.reportOptions"
                      }
                      onSubmit={({
                        filterValues,
                        reportType,
                        category,
                        reportId,
                        navigate,
                        reportFilters,
                      }) => {
                        setIsReportBeingModified(true);
                        createModifyCustomReportWithFilters({
                          reportName,
                          filterValues,
                          reportType: REPORT_TYPES.CUSTOM,
                          category: parseInt(categoryId as string),
                          reportId,
                          navigate,
                          selectedFields: selectedFields.map((field) => {
                            return {
                              field: Number(field.id),
                              field_title: field.title,
                            } as ModifyCustomReportPayloadField;
                          }),
                          reportFilters,
                          finallyCallback: () => {
                            setIsReportBeingModified(false);
                            dispatch(
                              setModalsData({
                                ...modalsState,
                                showModalNewCustomReportStep3: false,
                              })
                            );
                          },
                        } as TCreateCustomReportCallbackProps);
                        // return;
                        // hideModal();

                        // // // navigate to generate report
                        // navigateOnReportOptionsChange({ filterValues, reportType, category, reportId, navigate, reportFilters });

                        // // should we really do this .......?
                        // resetCreateCustomReportForms();
                      }}
                      onClose={() => {
                        setSelectedFields([]); // on close, reset selected fields
                      }}
                    />
                  )}
              </>
            )}
            {/* Share Button */}
            {(reportType === REPORT_TYPES.CUSTOM ||
              reportType === REPORT_TYPES.NEW_CUSTOM) && (
              <>
                <div
                  className="relative"
                  onClick={() => setShowShareDropdown(!showShareDropdown)}
                >
                  <AdminFormFieldDropdownButton
                    wrapperClass={`border-accent-blue-pale bg-accent-blue-pale text-accent-blue-dark hover:bg-accent-blue-dark hover:text-white${
                      showShareDropdown ? " z-modal" : ""
                    }`}
                    label={tMain("share")}
                    showDropdownIcon={showShareDropdown}
                    setShowDropdownIcon={setShowShareDropdown}
                  />
                  <SortingFilter
                    showSortingDropdown={showShareDropdown}
                    setShowSortingDropdown={setShowShareDropdown}
                    sortingOptions={SHARE_OPTIONS}
                    selectedSorting={""}
                    setSelectedSorting={(value) => {
                      {
                        value === SHARE_OPTIONS[0].value
                          ? dispatch(
                              setModalsData({
                                ...modalsState,
                                showInternalShareReportModal: true,
                              })
                            )
                          : dispatch(
                              setModalsData({
                                ...modalsState,
                                showShareViaEmailReportModal: true,
                              })
                            );
                      }
                    }}
                    hasCheckIcon={false}
                  />
                </div>
                {modalsState.showInternalShareReportModal && (
                  <ModalShareReportInternally
                    reportId={parseInt(reportId as string)}
                  />
                )}{" "}
                {/*//TODO: changed reportId to dynamic value */}
                {modalsState.showShareViaEmailReportModal && (
                  <ModalShareViaEmail
                    reportType={reportType}
                    reportId={parseInt(reportId as string)}
                    columns={savedColumns ?? allColumns}
                    isSummaryDependent={
                      dataSpecificReport?.is_summary_dependent
                    }
                    downloadFormats={dataSpecificReport?.download_formats}
                  />
                )}{" "}
                {/*//TODO: changed reportId to dynamic value */}
              </>
            )}
            {/* Download Button */}
            <div
              className="relative"
              onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
            >
              <AdminFormFieldDropdownButton
                wrapperClass={`border-accent-blue-pale bg-accent-blue-pale text-accent-blue-dark hover:bg-accent-blue-dark hover:text-white${
                  showDownloadDropdown ? " z-modal" : ""
                }`}
                label={tMain("download")}
                showDropdownIcon={showDownloadDropdown}
                setShowDropdownIcon={setShowDownloadDropdown}
                forcedIcon={
                  processId !== null ? svgIcons.LoadingAnimation : null
                }
              />
              <SortingFilter
                showSortingDropdown={showDownloadDropdown}
                setShowSortingDropdown={setShowDownloadDropdown}
                sortingOptions={downloadOptionsFor(
                  dataSpecificReport?.download_formats
                ).map((item) => {
                  const selectedColumns = savedColumns ?? allColumns;
                  return {
                    ...item,
                    ...(item.value === "pdf" &&
                      selectedColumns.length - 1 >
                        APP_CONFIG.REPORTS.MAX_COLS_FOR_PDF_DOWNLOAD && {
                        // Subtract 1 for Sr. No. column
                        label: `${item.label} (${tReports(
                          "too_many_columns"
                        )})`,
                        disabled: true, // Disable PDF download if visible colums exceeds MAX_COLS_FOR_PDF_DOWNLOAD
                        title: tReports("pdf_unavailable_too_many_columns"),
                      }),
                  };
                })}
                selectedSorting={""}
                setSelectedSorting={(format) => onDownloadReportClick(format)}
                hasCheckIcon={false}
              />
            </div>
            {/* Delete Button */}
            {(reportType === REPORT_TYPES.CUSTOM ||
              reportType === REPORT_TYPES.NEW_CUSTOM) && (
              <>
                <AdminFormFieldSubmit
                  label={tMain("delete")}
                  variant="danger"
                  type="button"
                  onClick={() => {
                    dispatch(
                      setModalsData({
                        ...modalsState,
                        showDeleteConfirmation: true,
                      })
                    );
                  }}
                />
                <ModalDeleteConfirmation
                  handleDeleteAdmin={handleDeleteCustomReport}
                />
              </>
            )}
          </div>
        }
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4 relative`}>
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <pre className="absolute text-[8px] -top-4 left-96 ml-48 opacity-50 z-20 pointer-events-none touch-none">
            initialLoad: {initialLoad?.toString()}
            <br />
            paramJson:
            <br />
            {JSON.stringify(paramJson, null, 2)}
            <br />
            deviceIdIsMutliSelect:
            <br />
            {deviceIdIsMutliSelect?.toString()}
            <br />
            deviceGroupIsMultiSelect:
            <br />
            {deviceGroupIsMultiSelect?.toString()}
            <br />
            paramJsonForReportOptions:
            <br />
            {JSON.stringify(paramJsonForReportOptions, null, 2)}
            <br />
            listingQueryParams.reports:
            <br />
            {JSON.stringify(listingQueryParams.reports, null, 2)}
          </pre>
        )}

        {/* Sub Header Bar */}
        <div className="relative">
          <SubHeaderBar
            reportType={reportType}
            HeaderLine1={
              categoryId === REPORT_CATEGORIES.OTHERS_DASHBOARD_WIDGETS ||
              !start_time || // TODO: need to check if this is correct
              !dataSpecificReport?.report_filters?.find(
                (filter) => filter.param === "start_time"
              ) ? ( // don't show date range if start_time is not a filter
                <></>
              ) : (
                <div className="text-sm leading-4">
                  <span className="font-semibold">
                    {tReports("label_date_range")}:
                  </span>{" "}
                  {!!restParams?.time_range ? (
                    tReports(`label_time_range_${restParams?.time_range}`)
                  ) : (
                    <>
                      <span className="relative">
                        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                          <pre className="absolute left-0 bottom-full text-[8px] leading-[8px] text-right">
                            API:{" "}
                            {
                              dataSpecificReport?.report_filters?.find(
                                (filter) => filter.param === "start_time"
                              )?.value
                            }
                            <br />
                            paramJson: {start_time}
                          </pre>
                        )}
                        {!!start_time
                          ? formattedDateTimeBasedOnTimezone(
                              dbTimeToJsTime(parseInt(`${start_time}`)),
                              paramJson?.timezone || tzForDateFns,
                              "MM/dd/yyyy (hh:mmaa)"
                            )
                          : ""}
                      </span>
                      {" - "}
                      <span className="relative">
                        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                          <pre className="absolute left-0 bottom-full text-[8px] leading-[8px] text-right">
                            API:{" "}
                            {
                              dataSpecificReport?.report_filters?.find(
                                (filter) => filter.param === "end_time"
                              )?.value
                            }
                            <br />
                            paramJson: {end_time}
                          </pre>
                        )}
                        {!!end_time
                          ? formattedDateTimeBasedOnTimezone(
                              dbTimeToJsTime(parseInt(`${end_time}`)),
                              paramJson?.timezone || tzForDateFns,
                              "MM/dd/yyyy (hh:mmaa)"
                            )
                          : ""}
                      </span>
                      {` (${paramJson?.timezone || tzForDateFns})`}
                    </>
                  )}
                </div>
              )
            }
            HeaderLine2={
              <ReportHeaderFiltersDisplayLabel
                paramJson={paramJson}
                vehiclesList={vehiclesList}
              />
            }
            filterButtonCallback={
              // display filters button only if there are filters
              !!dataSpecificReport?.report_filters?.length &&
              dataSpecificReport?.report_filters?.length > 0
                ? () => {
                    dispatch(
                      setModalsData({
                        ...modalsState,
                        showUpdateReportOptions: true,
                      })
                    );
                  }
                : undefined
            }
            filterResetCallback={
              !!listingQueryParams.reports.ordering
                ? () => {
                    dispatch(
                      setListingQueryParams({
                        ...listingQueryParams,
                        reports: {
                          ...orgReportQueryParams,
                          ordering: "",
                        },
                      })
                    );
                  }
                : undefined
            }
            ElementAlongsideFilterButton={
              <>
                {!reportIsUninitialized && !isError && (
                  <>
                    {colorModeAvailable && (
                      <ColorModeButton
                        active={colorMode}
                        disabled={isFetching && !colorModeLoadingMore}
                        onClick={toggleColorMode}
                      />
                    )}
                    <EditListingColumnsButton
                      disabled={isFetching}
                      positioningClasses="relative"
                      roundedSize="lg"
                      sizeShapeClasses="w-10 py-2"
                    />
                    {(reportType === REPORT_TYPES.CUSTOM ||
                      reportType === REPORT_TYPES.NEW_CUSTOM) && (
                      <SortColumnsButton
                        disabled={isFetching}
                        onClick={handleEditReportClick}
                        positioningClasses="relative"
                        roundedSize="lg"
                        sizeShapeClasses="w-10 py-2"
                      />
                    )}
                  </>
                )}
              </>
            }
          />

          {/* Advanced Filters Bar */}
          {reportType !== REPORT_TYPES.STANDARD && (
            <AdvancedFiltersBar
              onResetAdvancedFilter={handleResetReportAdvancedFilter}
            />
          )}

          {/* Advance Filter Popup */}
          <ErrorBoundaryForSection>
            {modalsState?.showAdvanceFilterPopup && (
              <AdvanceFilterPopUp
                reportId={parseInt(orgReportQueryParams.id as string)}
                columns={allColumns}
                reportFilters={dataSpecificReport?.report_filters || []}
                handleApplyReportAdvancedFilter={
                  handleApplyReportAdvancedFilter
                }
              />
            )}
          </ErrorBoundaryForSection>

          {/* Update Report Options Modal */}
          {modalsState?.showUpdateReportOptions && (
            <>
              {/* Disabled as this was causing both the dropdown and modal to close when clicking outside the modal.
                  However, the client wanted only the dropdown to close, and not the modal */}
              {/*
              <div
                className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay"
                onClick={() => {
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      showUpdateReportOptions: false,
                    })
                  );
                }}
              />
              */}
              <div className="absolute top-0 right-0 p-6 w-[480px] bg-white rounded-xl shadow-lg z-modal">
                <div className="flex justify-between items-center mb-8">
                  <h3 className="text-xl font-bold">
                    {tReports("options_filter.heading")}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      dispatch(
                        setModalsData({
                          ...modalsState,
                          showUpdateReportOptions: false,
                        })
                      );
                      dispatch(
                        setListingQueryParams({
                          ...listingQueryParams,
                          reports: {
                            ...orgReportQueryParams,
                            start_time,
                            end_time,
                            ...filteredAPIParams,
                          },
                        })
                      );
                    }}
                  >
                    <img src={svgIcons.CloseIcon} alt="close" />
                  </button>
                </div>
                <ReportOptions
                  reportType={reportType as keyof typeof REPORT_TYPES}
                  reportId={parseInt(reportId as string)}
                  reportFilters={{
                    ...(reportType !== REPORT_TYPES.STANDARD ||
                    deviceIdIsMutliSelect ||
                    deviceGroupIsMultiSelect
                      ? paramJsonForReportOptions
                      : paramJson),
                  }}
                  customSubmitLabel={tReports("options_filter.update_button")}
                  onSubmit={handleUpdateReportOptions}
                  scrollingWithinModal={true}
                />
              </div>
            </>
          )}
        </div>
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <pre className="absolute bottom-0 ml-4 mt-20 text-xs h-72 overflow-hidden opacity-50 z-10 pointer-events-none touch-none">
            savedColumns:
            <br />
            {JSON.stringify(savedColumns, null, 2)}
            <br />
            <br />
            reportVisibleColumns:
            <br />
            {JSON.stringify(reportVisibleColumns, null, 2)}
          </pre>
        )}
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <pre className="absolute ml-4 mt-20 right-0 text-[8px] opacity-50 z-10 pointer-events-none touch-none">
            reportType: {reportType} CONDITIONS FOR SKIP
            <br />
            {reportType !== REPORT_TYPES.STANDARD && (
              <>
                <span>
                  !orgReportQueryParams ={" "}
                  {!orgReportQueryParams ? "true" : "false"} ||{" "}
                </span>
                <br />
                <span>
                  orgReportQueryParams.id === "" ={" "}
                  {orgReportQueryParams.id === "" ? "true" : "false"} ||{" "}
                </span>
                <br />
                <span>
                  !dataSpecificReport?.report_fields ={" "}
                  {!dataSpecificReport?.report_fields ? "true" : "false"} ||{" "}
                </span>
                <br />
                <span>
                  isFetchingSpecificReport ={" "}
                  {isFetchingSpecificReport ? "true" : "false"}
                </span>
              </>
            )}
            <br />
            {reportType === REPORT_TYPES.STANDARD && (
              <>
                <span>
                  !orgReportQueryParams ={" "}
                  {!orgReportQueryParams ? "true" : "false"} ||{" "}
                </span>
                <br />
                <span>
                  isFetchingSpecificReport ={" "}
                  {isFetchingSpecificReport ? "true" : "false"}
                </span>
              </>
            )}
          </pre>
        )}

        {/* Listing Sub Header */}
        <div className="py-4 mt-4 relative">
          {isError ? (
            <div className="px-28 py-10 text-center">
              <h2 className="font-bold text-2xl text-gray-400">
                {isDuplicateQueryError
                  ? tMain("fatalErrors.duplicateReportQuery.heading")
                  : tMain("fatalErrors.singleReportError.heading")}
              </h2>
              <p className="text-gray-400 mt-2">
                {isDuplicateQueryError
                  ? reportErrorData?.error ||
                    tMain("fatalErrors.duplicateReportQuery.description")
                  : tMain("fatalErrors.singleReportError.description")}
              </p>
            </div>
          ) : (
            <>
              {/* Moved sort and show/hide buttons alongside filter options button */}
              <ReportTable
                viewportHeightOffsetClass={"max-h-single-report"}
                columns={allColumns}
                data={tableData || []}
                colorMode={colorMode}
                hidePagination={colorMode}
                onLoadMore={loadMoreColorModeRows}
                hasMore={colorModeHasMore}
                isLoadingMore={colorModeLoadingMore}
                count={
                  reportData?.count && reportData.count > 0
                    ? reportData.count
                    : cachedReportCount.filterFingerprint === currentFilterFingerprint &&
                      Date.now() - cachedReportCount.timestamp < CACHED_COUNT_STALENESS_THRESHOLD_MS
                    ? cachedReportCount.value
                    : 0
                }
                isLoading={
                  (isFetching && !colorModeLoadingMore) ||
                  isReportBeingModified ||
                  isFetchingReportAllFields
                }
                listingQueryParams={orgReportQueryParams}
                pageSize={orgReportQueryParams?.page_size}
                gotoPage={(page: number) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      reports: {
                        ...orgReportQueryParams,
                        page: page,
                      },
                    })
                  );
                }}
                setPageSize={(size: number) => {
                  if (reportType) {
                    dispatch(
                      setReportPageSize({
                        reportType: reportType,
                        pageSize: size,
                      })
                    );
                  }
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      reports: {
                        ...orgReportQueryParams,
                        page_size: size,
                        page: 1,
                      },
                    })
                  );
                }}
                orderBy={onSortClick}
                rawData={tableSourceRows || []}
                reportFilters={dataSpecificReport?.report_filters || []}
                reportFields={dataSpecificReport?.report_fields || []}
                reportType={reportType as string}
                onApplyReportColumnFilter={handleApplyReportColumnFilter}
                onApplyReportColumnCalc={handleApplyReportColumnCalculation}
                // key added to refresh the table when the report fields or filters are updated
                // (this collapses any expanded group rows and helps in refreshing the table)
                key={`report-table-${reportId}_${JSON.stringify(dataSpecificReport?.report_fields)}_${JSON.stringify(dataSpecificReport?.report_filters)}`} // column search keywork part moved to ReportTableRow and ReportTableRowGrandTotal calls in ReportTable
              />
              <ModalEditListingColumns
                columns={columnsVisibility}
                setColumns={setColumnsVisibility}
                lsKey={localStorageKeys.columns.Reports(
                  reportType ?? "",
                  categoryId ?? "",
                  reportId ?? ""
                )}
              />
            </>
          )}
        </div>
      </div>
      {showErrorModal && (
        <ReportErrorModal
          heading={tReports(
            `${isError ? "reportError" : "reportTakingLonger"}.heading`
          )}
          subHeading={tReports(
            `${isError ? "reportError" : "reportTakingLonger"}.description`
          )}
          type={isError ? "error" : "longProcess"}
          onHide={() => {
            console.log("hide");
            setShowErrorModal(false);
          }}
          showOverlay={!isError}
          ActionButtons={
            <div className="flex gap-4 my-2">
              {downloadOptionsFor(dataSpecificReport?.download_formats)
                .map((item) => {
                  return {
                    ...item,
                    ...(item.value === "pdf" &&
                      !!allColumns &&
                      allColumns.length - 1 >
                        APP_CONFIG.REPORTS.MAX_COLS_FOR_PDF_DOWNLOAD && {
                        // Subtract 1 for Sr. No. column
                        label: `${item.label} (${tReports("too_many_columns")})`,
                        disabled: true, // Disable PDF download if visible colums exceeds MAX_COLS_FOR_PDF_DOWNLOAD
                        title: tReports("pdf_unavailable_too_many_columns"),
                      }),
                  };
                })
                .map((item, index) => (
                  <button
                    className={`grow px-3 py-2 space-x-3 flex items-center justify-center rounded-lg bg-accent-blue-paleO66 enabled:hover:bg-accent-blue-dark text-gray-500 enabled:hover:text-white disabled:bg-gray-100 disabled:text-gray-400`}
                    disabled={item?.disabled === true}
                    title={item?.title}
                    key={index}
                    type="button"
                    onClick={() => {
                      setShowErrorModal(false);
                      onDownloadReportClick(item.value);
                    }}
                    role="menuitem"
                  >
                    <span className={`block rounded-lg text-sm text-left`}>
                      {item.label}
                    </span>
                  </button>
                ))}
            </div>
          }
        />
      )}
      {modalType !== null && (
        <BulkStatusModal
          heading={getModalHeading}
          subHeading={
            modalType === "longProcess"
              ? tToast("longProcess")
              : modalType === "error"
                ? tToast("error_details")
                : undefined
          }
          type={modalType}
          fileName={
            processStatusData?.output_files &&
            processStatusData?.output_files?.length > 0
              ? processStatusData?.output_files[0].name
              : ""
          }
          onHide={onModalActionClick}
        />
      )}
    </DragDropContext>
  );
};

export default ScreenSingleReport;
